//src/pages/riscos/AreasPage.tsx
import { useState } from 'react';
import { Plus, MapPin, Pencil, Trash2, MoreHorizontal } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PageHeader } from '@/components/shared/PageHeader';
import { PermissionButton } from '@/components/shared/PermissionButton';
import { CrudDialog } from '@/components/shared/CrudDialog';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { mockAreas } from '@/mocks/data';
import { toast } from 'sonner';
import { PERMISSIONS } from '@/lib/permissions';
import { useAuth } from '@/contexts/AuthContext';
import type { Area } from '@/types';

export default function AreasPage() {
  const { user } = useAuth();
  const canWrite = user?.permissions?.includes(PERMISSIONS.AREAS_WRITE) ?? false;

  const [areas, setAreas] = useState<Area[]>(mockAreas);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [editing, setEditing] = useState<Partial<Area> | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const roots = areas.filter(a => !a.parent_id);
  const children = (parentId: string) => areas.filter(a => a.parent_id === parentId);

  const openNew = () => { if (!canWrite) return; setEditing({ name: '', description: '', parent_id: undefined }); setDialogOpen(true); };
  const openEdit = (a: Area) => { if (!canWrite) return; setEditing({ ...a }); setDialogOpen(true); };
  const openDelete = (id: string) => { if (!canWrite) return; setSelectedId(id); setConfirmOpen(true); };

  const handleSave = () => {
    if (!editing?.name) { toast.error('Nome é obrigatório'); return; }
    if (editing.id) {
      setAreas(prev => prev.map(a => a.id === editing.id ? { ...a, ...editing } as Area : a));
      toast.success('Área atualizada');
    } else {
      const newArea: Area = { ...editing, id: `area-${Date.now()}`, tenant_id: 'tenant-001', created_at: new Date().toISOString() } as Area;
      setAreas(prev => [...prev, newArea]);
      toast.success('Área criada');
    }
  };

  const handleDelete = () => {
    if (!selectedId) return;
    setAreas(prev => prev.filter(a => a.id !== selectedId && a.parent_id !== selectedId));
    toast.success('Área removida');
  };

  return (
    <div>
      <PageHeader title="Áreas e Setores" description="Cadastro hierárquico de ambientes e áreas">
        <PermissionButton permission={PERMISSIONS.AREAS_WRITE} onClick={openNew}><Plus className="mr-2 h-4 w-4" /> Nova Área</PermissionButton>
      </PageHeader>

      <div className="grid gap-3 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
        {roots.map(area => (
          <Card key={area.id} className="hover:border-primary/30 transition-colors">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-primary" />
                  {area.name}
                </CardTitle>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-7 w-7"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem disabled={!canWrite} onClick={() => openEdit(area)}><Pencil className="mr-2 h-4 w-4" /> Editar</DropdownMenuItem>
                    <DropdownMenuItem disabled={!canWrite} onClick={() => openDelete(area.id)} className="text-destructive"><Trash2 className="mr-2 h-4 w-4" /> Excluir</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-2">{area.description}</p>
              {children(area.id).length > 0 && (
                <div className="pl-4 border-l-2 border-primary/20 space-y-1">
                  {children(area.id).map(child => (
                    <div key={child.id} className="flex items-center justify-between">
                      <p className="text-sm text-muted-foreground">{child.name}</p>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" className="h-6 w-6" disabled={!canWrite} onClick={() => openEdit(child)}><Pencil className="h-3 w-3" /></Button>
                        <Button variant="ghost" size="icon" className="h-6 w-6" disabled={!canWrite} onClick={() => openDelete(child.id)}><Trash2 className="h-3 w-3 text-destructive" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      <CrudDialog open={dialogOpen} onOpenChange={setDialogOpen} title={editing?.id ? 'Editar Área' : 'Nova Área'} onSave={handleSave}>
        <div className="space-y-3">
          <div><Label>Nome</Label><Input value={editing?.name || ''} onChange={e => setEditing(prev => prev ? { ...prev, name: e.target.value } : prev)} /></div>
          <div><Label>Descrição</Label><Input value={editing?.description || ''} onChange={e => setEditing(prev => prev ? { ...prev, description: e.target.value } : prev)} /></div>
          <div><Label>Área Pai (opcional)</Label>
            <Select value={editing?.parent_id || 'none'} onValueChange={v => setEditing(prev => prev ? { ...prev, parent_id: v === 'none' ? undefined : v } : prev)}>
              <SelectTrigger><SelectValue placeholder="Nenhuma" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Nenhuma (raiz)</SelectItem>
                {areas.filter(a => !a.parent_id && a.id !== editing?.id).map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </CrudDialog>

      <ConfirmDialog open={confirmOpen} onOpenChange={setConfirmOpen} title="Excluir Área" description="Sub-áreas também serão removidas. Deseja continuar?" onConfirm={handleDelete} confirmLabel="Excluir" />
    </div>
  );
}
